import {
  REVERB_AMOUNT_STEP,
  REVERB_AMOUNT_STEPS,
} from "./reverb-steps";

function bitDepthFromAmount(amount) {
  return Math.max(3, Math.round(16 - amount * 12));
}

function bassBoostLabelFromAmount(amount) {
  return `+${Math.round(amount * 15)} dB`;
}

function phaserLabelFromAmount(amount) {
  return `${Math.round((0.2 + amount * 1.6) * 10) / 10} Hz`;
}

function echoLabelFromAmount(amount) {
  const ms = Math.round((0.15 + amount * 0.35) * 1000);
  return `${ms} ms`;
}

function reverbLabelForAmount(amount) {
  const index = Math.round(amount / REVERB_AMOUNT_STEP);
  return REVERB_AMOUNT_STEPS[index]?.label ?? "None";
}

export const AUDIO_MODE_PROPS = [
  { key: "reverb", isActive: (props) => (props.reverbAmount ?? 0) > 0 },
  { key: "highPass", prop: "highPassMode" },
  { key: "lowPass", prop: "lowPassMode" },
  { key: "bitcrusher", prop: "bitcrusherMode" },
  { key: "bassBoost", prop: "bassBoostMode" },
  { key: "phaser", prop: "phaserMode" },
  { key: "echo", prop: "echoMode" },
  { key: "lofiTape", prop: "lofiTapeMode" },
  { key: "vinylCrackle", prop: "vinylCrackleMode" },
  { key: "rainy", prop: "rainyMode" },
  { key: "coffeeShop", prop: "coffeeShopMode" },
  { key: "seashore", prop: "seashoreMode" },
];

export function buildAudioModes(props) {
  const {
    reverbAmount,
    onReverbAmountChange,
    lowPassMode,
    lowPassCutoff,
    highPassMode,
    highPassCutoff,
    bitcrusherMode,
    bitcrusherAmount,
    bassBoostMode,
    bassBoostAmount,
    phaserMode,
    phaserAmount,
    echoMode,
    echoAmount,
    lofiTapeMode,
    lofiHissVolume,
    rainyMode,
    rainVolume,
    coffeeShopMode,
    coffeeShopVolume,
    seashoreMode,
    seashoreVolume,
    vinylCrackleMode,
    vinylCrackleVolume,
    onLowPassModeChange,
    onLowPassCutoffChange,
    onHighPassModeChange,
    onHighPassCutoffChange,
    onBitcrusherModeChange,
    onBitcrusherAmountChange,
    onBassBoostModeChange,
    onBassBoostAmountChange,
    onPhaserModeChange,
    onPhaserAmountChange,
    onEchoModeChange,
    onEchoAmountChange,
    onLofiTapeModeChange,
    onLofiHissVolumeChange,
    onRainyModeChange,
    onRainVolumeChange,
    onCoffeeShopModeChange,
    onCoffeeShopVolumeChange,
    onSeashoreModeChange,
    onSeashoreVolumeChange,
    onVinylCrackleModeChange,
    onVinylCrackleVolumeChange,
  } = props;

  const amount = typeof reverbAmount === "number" ? reverbAmount : 0;

  return [
    {
      key: "reverb",
      label: "Reverb Depth",
      active: amount > 0,
      onToggle: () =>
        onReverbAmountChange(amount > 0 ? 0 : parseFloat((0.45).toFixed(4))),
      slider: {
        label: "Amount",
        min: 0,
        max: 1,
        step: REVERB_AMOUNT_STEP,
        value: amount,
        format: reverbLabelForAmount,
        onChange: (v) => onReverbAmountChange(v),
      },
    },
    {
      key: "highPass",
      label: "High Pass",
      active: highPassMode,
      onToggle: () => onHighPassModeChange(!highPassMode),
      slider: {
        label: "Cutoff",
        min: 80,
        max: 3500,
        step: 50,
        value: typeof highPassCutoff === "number" ? highPassCutoff : 500,
        format: (v) => `${Math.round(v)} Hz`,
        onChange: (v) => onHighPassCutoffChange(v),
      },
    },
    {
      key: "lowPass",
      label: "Low Pass",
      active: lowPassMode,
      onToggle: () => onLowPassModeChange(!lowPassMode),
      slider: {
        label: "Cutoff",
        min: 200,
        max: 3500,
        midValue: 1000,
        step: 50,
        value: typeof lowPassCutoff === "number" ? lowPassCutoff : 1000,
        defaultValue: 1000,
        format: (v) => `${Math.round(v)} Hz`,
        onChange: (v) => onLowPassCutoffChange(v),
      },
    },
    {
      key: "bitcrusher",
      label: "Down Sampler",
      active: bitcrusherMode,
      onToggle: () => onBitcrusherModeChange(!bitcrusherMode),
      slider: {
        label: "Crush",
        min: 0,
        max: 1,
        step: 0.01,
        value:
          typeof bitcrusherAmount === "number" ? bitcrusherAmount : 0.45,
        format: (v) => `${bitDepthFromAmount(v)}-bit`,
        onChange: (v) => onBitcrusherAmountChange(v),
      },
    },
    {
      key: "bassBoost",
      label: "Bass Boost",
      active: bassBoostMode,
      onToggle: () => onBassBoostModeChange(!bassBoostMode),
      slider: {
        label: "Boost",
        min: 0,
        max: 1,
        step: 0.01,
        value: typeof bassBoostAmount === "number" ? bassBoostAmount : 0.5,
        format: bassBoostLabelFromAmount,
        onChange: (v) => onBassBoostAmountChange(v),
      },
    },
    {
      key: "phaser",
      label: "Phase Shifter",
      active: phaserMode,
      onToggle: () => onPhaserModeChange(!phaserMode),
      slider: {
        label: "Sweep",
        min: 0,
        max: 1,
        step: 0.01,
        value: typeof phaserAmount === "number" ? phaserAmount : 0.5,
        format: phaserLabelFromAmount,
        onChange: (v) => onPhaserAmountChange(v),
      },
    },
    {
      key: "echo",
      label: "Echo Delay",
      active: echoMode,
      onToggle: () => onEchoModeChange(!echoMode),
      slider: {
        label: "Delay time",
        min: 0,
        max: 1,
        step: 0.01,
        value: typeof echoAmount === "number" ? echoAmount : 0.5,
        format: echoLabelFromAmount,
        onChange: (v) => onEchoAmountChange(v),
      },
    },
    {
      key: "lofiTape",
      label: "Lofi Tape",
      active: lofiTapeMode,
      onToggle: () => onLofiTapeModeChange(!lofiTapeMode),
      slider: {
        label: "Hiss volume",
        min: 0,
        max: 1,
        step: 0.01,
        value: typeof lofiHissVolume === "number" ? lofiHissVolume : 1,
        format: (v) => `${Math.round(v * 100)}%`,
        onChange: (v) => onLofiHissVolumeChange(v),
      },
    },
    {
      key: "vinylCrackle",
      label: "Vinyl Crackle",
      active: vinylCrackleMode,
      onToggle: () => onVinylCrackleModeChange(!vinylCrackleMode),
      slider: {
        label: "Crackle volume",
        min: 0,
        max: 1,
        step: 0.01,
        value:
          typeof vinylCrackleVolume === "number" ? vinylCrackleVolume : 0.55,
        format: (v) => `${Math.round(v * 100)}%`,
        onChange: (v) => onVinylCrackleVolumeChange(v),
      },
    },
    {
      key: "rainy",
      label: "Rainy Day",
      active: rainyMode,
      onToggle: () => onRainyModeChange(!rainyMode),
      slider: {
        label: "Rain volume",
        min: 0,
        max: 1,
        step: 0.01,
        value: typeof rainVolume === "number" ? rainVolume : 0.6,
        format: (v) => `${Math.round(v * 100)}%`,
        onChange: (v) => onRainVolumeChange(v),
      },
    },
    {
      key: "coffeeShop",
      label: "Coffee Bar",
      active: coffeeShopMode,
      onToggle: () => onCoffeeShopModeChange(!coffeeShopMode),
      slider: {
        label: "Ambient volume",
        min: 0,
        max: 1,
        step: 0.01,
        value:
          typeof coffeeShopVolume === "number" ? coffeeShopVolume : 0.6,
        format: (v) => `${Math.round(v * 100)}%`,
        onChange: (v) => onCoffeeShopVolumeChange(v),
      },
    },
    {
      key: "seashore",
      label: "Seashore",
      active: seashoreMode,
      onToggle: () => onSeashoreModeChange(!seashoreMode),
      slider: {
        label: "Wave volume",
        min: 0,
        max: 1,
        step: 0.01,
        value: typeof seashoreVolume === "number" ? seashoreVolume : 0.6,
        format: (v) => `${Math.round(v * 100)}%`,
        onChange: (v) => onSeashoreVolumeChange(v),
      },
    },
  ];
}
